import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'src/components/ui/toast';

// components
import FrontLayout from 'src/components/layouts/FrontLayout';
import Button from 'src/components/ui/Button';
import TitledPanel from 'src/components/ui/TitledPanel';

// hooks
import useAuth from 'src/hooks/useAuth';

interface UnauthorizedPageProps {
  /** Custom message to display. Defaults to standard access denied message. */
  message?: ReactNode;
  /** Custom buttons to display. If not provided, shows default Log out and Return to Home buttons. */
  buttons?: ReactNode;
}

export default function UnauthorizedPage({ message, buttons }: UnauthorizedPageProps) {
  const { logout } = useAuth();

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      toast.error('An error occurred during logout');
    }
  };

  const defaultMessage = (
    <>
      You don't have access to this page. If you believe you should have access, please contact the resource owner, your
      administrator or CompliMind Support at support@complimind.co.uk.
    </>
  );

  const defaultButtons = (
    <div className="flex gap-2">
      <Button variant="outline" fullWidth onClick={handleLogout}>
        Log out
      </Button>
      <Button variant="primary" fullWidth onClick={() => navigate('/home')}>
        Return to Home
      </Button>
    </div>
  );

  return (
    <FrontLayout>
      <TitledPanel
        title={<h1 className="text-lg font-semibold text-content-default">Access denied</h1>}
        className="gap-4"
      >
        <p>{message ?? defaultMessage}</p>
        {buttons ?? defaultButtons}
      </TitledPanel>
    </FrontLayout>
  );
}
